import { useState, useMemo, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import { SchemaService, DynamicModuleService } from "@/services/APIs";
import { notify } from "@/components/common/Notification";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { checkFormChanges } from "@/lib/checkFormChanges";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { ConfigProvider } from "antd";

const EMPLOYEE_RATE_SCHEMA_NAME = "PTS Employee Rate";

interface AddEmployeeRateModalProps {
  open: boolean;
  onClose: () => void;
  rateId?: string | null;
}

const AddEmployeeRateModal = ({
  open,
  onClose,
  rateId,
}: AddEmployeeRateModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = !!rateId;
  const form = useMemo(() => createForm({}), []);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Fetch schema information
  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", EMPLOYEE_RATE_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(EMPLOYEE_RATE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_RATE_SCHEMA_NAME && open,
  });

  // Fetch rate information for edit mode
  const { data: rateInfo, isLoading: rateLoading } = useQuery({
    queryKey: ["dynamic-model", EMPLOYEE_RATE_SCHEMA_NAME, rateId],
    queryFn: () => {
      if (!rateId) {
        throw new Error("Rate ID is required for edit mode");
      }
      return DynamicModuleService.getItemById(
        EMPLOYEE_RATE_SCHEMA_NAME,
        rateId
      );
    },
    enabled: isEditMode && !!rateId && open,
  });

  // Set form initial values when in edit mode
  useEffect(() => {
    if (isEditMode && rateInfo && open) {
      form.setInitialValues(removeMetaFields(rateInfo));
    } else if (!isEditMode && open) {
      // Reset form when opening in add mode
      form.reset();
    }
  }, [isEditMode, rateInfo, form, open]);

  const handleSubmit = async () => {
    try {
      await form.validate();
      setIsSubmitting(true);
      const values = removeMetaFields(form.values);

      if (isEditMode && rateId) {
        // Check for changes before updating
        if (!checkFormChanges(values, rateInfo, notify)) {
          setIsSubmitting(false);
          return;
        }
        const response = await DynamicModuleService.updateItem(
          EMPLOYEE_RATE_SCHEMA_NAME,
          rateId,
          values
        );
        if (response) {
          //   notify.success("Employee rate updated successfully");
          onClose();
        }
      } else {
        const response = await DynamicModuleService.createItem(
          EMPLOYEE_RATE_SCHEMA_NAME,
          values
        );
        if (response) {
          notify.success("Employee rate created successfully");
          onClose();
        }
      }
    } catch (error: any) {
      notify.error(
        error?.message ??
          `Failed to ${isEditMode ? "update" : "create"} employee rate`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = schemaLoading || (isEditMode && rateLoading);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-xl flex-col bg-white p-0 sm:w-full">
        <DialogHeader className="flex-shrink-0 px-6 pb-4 pt-6">
          <DialogTitle>
            {isEditMode ? "Edit Employee Rate" : "Add Employee Rate"}
          </DialogTitle>
          <DialogDescription>
            Set billing and cost rates with effective dates for rate history
            tracking.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={modalContentRef}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6"
        >
          <ConfigProvider
            getPopupContainer={() => {
              return modalContentRef.current ?? document.body;
            }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="">
                <SchemaFormView
                  form={form}
                  schemaJson={schemaInfo?.json ?? schemaInfo}
                />
              </div>
            )}
          </ConfigProvider>
        </div>

        <DialogFooter className="flex-shrink-0 flex-col gap-2 border-t px-6 pb-6 pt-4 sm:flex-row sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {isSubmitting
              ? "Saving..."
              : isEditMode
                ? "Update Rate"
                : "Save Rate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmployeeRateModal;
